/*****************************************************
Naam: ErrorBoundary.jsx
Locatie: /Dashboard/src/components/ErrorBoundary.jsx
Datum: 2026-01-06
Versie: 1.0.0
Auteur: Ragnar Standhardt
******************************************************
Beschrijving:
React Error Boundary component om crashes te voorkomen.
Vangt React errors op en toont een fallback UI.

Features:
- Catch alle React errors in child components
- Toon user-friendly error message
- Log error details naar console
- Reload knop om te herstellen
- Styling consistent met dashboard theme

Usage:
<ErrorBoundary>
  <YourComponent />
</ErrorBoundary>
******************************************************/

import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state zodat fallback UI wordt getoond
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('❌ Error Boundary caught an error:', error);
    console.error('Error Info:', errorInfo);
    
    // Update state met error details
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // TODO: Stuur error naar error tracking service (Sentry, LogRocket, etc.)
    // Example: logErrorToService(error, errorInfo);
  }

  handleReload = () => {
    // Reset state en herlaad
    this.setState({ 
      hasError: false,
      error: null,
      errorInfo: null
    });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-white shadow-lg p-8">
            {/* Error Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <svg 
                  className="w-10 h-10 text-red-600" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                  />
                </svg>
              </div>
            </div>

            {/* Error Title */}
            <h1 className="text-3xl font-bold text-gray-800 text-center mb-4">
              Er is iets misgegaan
            </h1>

            {/* Error Message */}
            <p className="text-gray-600 text-center mb-6">
              Het dashboard heeft een onverwachte fout ondervonden. 
              Probeer de pagina opnieuw te laden.
            </p>

            {/* Error Details (Development only) */}
            {this.state.error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200">
                <h3 className="font-semibold text-red-800 mb-2">
                  🐛 Development Info:
                </h3>
                <div className="text-sm text-red-700 font-mono overflow-auto max-h-40">
                  <p className="font-bold mb-1">Error:</p>
                  <p className="mb-3">{this.state.error.toString()}</p>
                  
                  {this.state.errorInfo && (
                    <>
                      <p className="font-bold mb-1">Component Stack:</p>
                      <pre className="whitespace-pre-wrap text-xs">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReload}
                className="px-6 py-3 bg-green-500 text-white font-semibold hover:bg-green-600 transition-colors shadow-md"
              >
                🔄 Pagina Herladen
              </button>
              
              <button
                onClick={() => window.history.back()}
                className="px-6 py-3 bg-gray-500 text-white font-semibold hover:bg-gray-600 transition-colors shadow-md"
              >
                ← Terug
              </button>
            </div>

            {/* Support Info */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-500">
                Als dit probleem blijft optreden, neem dan contact op met support.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Normaal renderen van children
    return this.props.children;
  }
}

export default ErrorBoundary;
